import React, { useEffect, useState } from 'react';
import { faqAPI } from '../../api/faq';
import { Plus, Edit2, Trash2, ChevronDown, ChevronRight, Search, HelpCircle, Eye, ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { toast } from 'sonner';

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

function CategoryModal({ category, onSave, onClose }) {
  const isEdit = Boolean(category?.id);
  const [form, setForm] = useState({
    name: category?.name || '',
    description: category?.description || '',
    icon: category?.icon || 'HelpCircle',
    order: category?.order ?? 0,
    is_active: category?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const icons = ['HelpCircle', 'Book', 'Shield', 'Settings', 'Users', 'Calendar', 'FileText', 'AlertTriangle', 'Star', 'Zap', 'Lock', 'Globe'];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="category-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b bg-soem-navy">
          <h3 className="font-bold text-white">{isEdit ? 'Editar Categoria' : 'Nueva Categoria'}</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="f-cat-name" className="soem-label block mb-1">Nombre <span className="text-red-500">*</span></label>
            <input id="f-cat-name" className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="cat-name-input" />
          </div>
          <div>
            <label htmlFor="f-cat-desc" className="soem-label block mb-1">Descripcion</label>
            <textarea id="f-cat-desc" className={`${inputCls} resize-none`} rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </div>
          <div>
            <label className="soem-label block mb-1">Icono</label>
            <div className="grid grid-cols-6 gap-2">
              {icons.map(ic => (
                <button key={ic} type="button" onClick={() => setForm({...form, icon: ic})}
                  className={`p-2 rounded-sm border-2 text-center text-xs transition-all ${form.icon === ic ? 'border-soem-primary bg-blue-50 text-soem-primary font-bold' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                  {ic.slice(0, 4)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-cat-order" className="soem-label block mb-1">Orden</label>
              <input id="f-cat-order" type="number" className={inputCls} value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" />
                Activo
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60" data-testid="cat-save-btn">
              {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Categoria'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function QuestionModal({ question, categories, onSave, onClose }) {
  const isEdit = Boolean(question?.id);
  const [form, setForm] = useState({
    category_id: question?.category_id || '',
    question: question?.question || '',
    answer: question?.answer || '',
    order: question?.order ?? 0,
    is_active: question?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="question-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b bg-soem-navy">
          <h3 className="font-bold text-white">{isEdit ? 'Editar Pregunta' : 'Nueva Pregunta'}</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="f-q-cat" className="soem-label block mb-1">Categoria <span className="text-red-500">*</span></label>
            <select id="f-q-cat" className={inputCls} value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} required data-testid="q-category-select">
              <option value="">— Seleccionar —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="f-q-text" className="soem-label block mb-1">Pregunta <span className="text-red-500">*</span></label>
            <textarea id="f-q-text" className={`${inputCls} resize-none`} rows={2} value={form.question} onChange={e => setForm({...form, question: e.target.value})} required data-testid="q-text-input" />
          </div>
          <div>
            <label htmlFor="f-q-answer" className="soem-label block mb-1">Respuesta <span className="text-red-500">*</span></label>
            <textarea id="f-q-answer" className={`${inputCls} resize-none`} rows={6} value={form.answer} onChange={e => setForm({...form, answer: e.target.value})} required placeholder="Soporta Markdown basico" data-testid="q-answer-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-q-order" className="soem-label block mb-1">Orden</label>
              <input id="f-q-order" type="number" className={inputCls} value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" />
                Activo
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60" data-testid="q-save-btn">
              {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Pregunta'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function FAQAdmin() {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [catModal, setCatModal] = useState(null);
  const [qModal, setQModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, qRes] = await Promise.all([faqAPI.getCategories(), faqAPI.getQuestions()]);
      setCategories((cRes.data.items || cRes.data || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
      setQuestions((qRes.data.items || qRes.data || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch {
      toast.error('Error al cargar FAQ');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const handleSaveCat = async (form) => {
    if (catModal?.id) {
      await faqAPI.updateCategory(catModal.id, form);
      toast.success('Categoria actualizada');
    } else {
      await faqAPI.createCategory(form);
      toast.success('Categoria creada');
    }
    load();
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm('Eliminar esta categoria y todas sus preguntas?')) return;
    try {
      await faqAPI.deleteCategory(id);
      toast.success('Categoria eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleSaveQ = async (form) => {
    if (qModal?.id) {
      await faqAPI.updateQuestion(qModal.id, form);
      toast.success('Pregunta actualizada');
    } else {
      await faqAPI.createQuestion(form);
      toast.success('Pregunta creada');
    }
    load();
  };

  const handleDeleteQ = async (id) => {
    if (!window.confirm('Eliminar esta pregunta?')) return;
    try {
      await faqAPI.deleteQuestion(id);
      toast.success('Pregunta eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const filteredCats = search
    ? categories.filter(c => {
        const catMatch = c.name.toLowerCase().includes(search.toLowerCase());
        const qMatch = questions.some(q => q.category_id === c.id &&
          (q.question.toLowerCase().includes(search.toLowerCase()) || q.answer?.toLowerCase().includes(search.toLowerCase())));
        return catMatch || qMatch;
      })
    : categories;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="faq-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Administracion de FAQ</h1>
          <p className="text-slate-500 text-sm mt-1">{categories.length} categorias, {questions.length} preguntas</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCatModal({})} className="soem-btn-secondary px-3 py-2 text-sm flex items-center gap-1.5" data-testid="new-cat-btn">
            <Plus size={14} /> Nueva Categoria
          </button>
          <button onClick={() => setQModal({})} className="soem-btn-primary px-3 py-2 text-sm flex items-center gap-1.5" data-testid="new-q-btn">
            <Plus size={14} /> Nueva Pregunta
          </button>
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
          placeholder="Buscar preguntas..." value={search} onChange={e => setSearch(e.target.value)} aria-label="Buscar en FAQ" data-testid="faq-search" />
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="soem-card h-20 animate-pulse bg-slate-100" />)}</div>
      ) : filteredCats.length === 0 ? (
        <div className="soem-card p-12 text-center">
          <HelpCircle size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{search ? 'Sin resultados.' : 'No hay categorias. Crea la primera.'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCats.map(cat => {
            const catQs = questions.filter(q => q.category_id === cat.id);
            const isOpen = expanded[cat.id];
            return (
              <div key={cat.id} className="soem-card overflow-hidden" data-testid={`cat-card-${cat.id}`}>
                <div className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggle(cat.id)}>
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown size={16} className="text-soem-primary" /> : <ChevronRight size={16} className="text-slate-400" />}
                    <div>
                      <h3 className="font-bold text-soem-navy text-sm">{cat.name}</h3>
                      {cat.description && <p className="text-slate-400 text-xs mt-0.5">{cat.description}</p>}
                    </div>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium">{catQs.length} preguntas</span>
                    {!cat.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Inactiva</span>}
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setCatModal(cat)} className="p-1.5 text-slate-400 hover:text-soem-primary rounded-sm" aria-label="Editar categoria"><Edit2 size={14} /></button>
                    <button onClick={() => handleDeleteCat(cat.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded-sm" aria-label="Eliminar categoria"><Trash2 size={14} /></button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {catQs.length === 0 ? (
                      <p className="px-5 py-4 text-slate-400 text-sm">Sin preguntas en esta categoria.</p>
                    ) : catQs.map(q => (
                      <div key={q.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50" data-testid={`q-row-${q.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-soem-navy font-medium truncate">{q.question}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Eye size={10} /> {q.views || 0}</span>
                            <span className="flex items-center gap-1"><ThumbsUp size={10} /> {q.useful || 0}</span>
                            <span className="flex items-center gap-1"><ThumbsDown size={10} /> {q.not_useful || 0}</span>
                            {!q.is_active && <span className="text-red-500">Inactiva</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                          <button onClick={() => setQModal(q)} className="p-1.5 text-slate-400 hover:text-soem-primary rounded-sm" aria-label="Editar pregunta"><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteQ(q.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded-sm" aria-label="Eliminar pregunta"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {catModal !== null && <CategoryModal category={catModal} onSave={handleSaveCat} onClose={() => setCatModal(null)} />}
      {qModal !== null && <QuestionModal question={qModal} categories={categories} onSave={handleSaveQ} onClose={() => setQModal(null)} />}
    </div>
  );
}
